import { PLAN_CATALOG, planAtLeast } from '@secretario/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { describeError } from '../../../lib/api/http.js';
import { updateSpecialistConfig, type PASpecialistFeedItem } from '../../../lib/api/team.js';
import { openPaywall, useEntitlements } from '../../../lib/plan.js';
import './news-alerts.css';

const FEED_KEY = ['pa-specialist-feed', 'noticias'];
const MAX = 10;

const when = (iso: string) =>
  new Date(iso).toLocaleString('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });

/** "Me avisa quando sair algo sobre…": assuntos do Completo e o que já saiu sobre eles. */
export function NewsAlerts({ topics, items }: { topics: string[]; items: PASpecialistFeedItem[] }) {
  const queryClient = useQueryClient();
  const entitlementsQ = useEntitlements();
  const pro = !!entitlementsQ.data && planAtLeast(entitlementsQ.data.plan, 'pro');
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function save(next: string[]) {
    setBusy(true);
    setError('');
    try {
      await updateSpecialistConfig('noticias', { alerts: next });
      await queryClient.invalidateQueries({ queryKey: FEED_KEY });
    } catch (failure) {
      setError(describeError(failure));
    } finally {
      setBusy(false);
    }
  }

  function add(event: FormEvent) {
    event.preventDefault();
    const topic = draft.replace(/\s+/g, ' ').trim();
    if (!pro) {
      openPaywall('news', 'noticias_alerta');
      return;
    }
    if (topic.length < 3 || topics.length >= MAX) return;
    setDraft('');
    void save([...topics, topic]);
  }

  return (
    <section className="pgs-section">
      <h2 className="pgs-h2">
        Alertas <small>{pro ? `${topics.length} de ${MAX}` : PLAN_CATALOG.pro.name}</small>
      </h2>
      <div className="pgs-note">
        <i aria-hidden="true">!</i>
        <span>
          <b>Me avisa quando sair algo sobre…</b>
          <small>
            Eu confiro as fontes a cada hora e separo aqui o que sair de novo sobre o seu assunto.
          </small>
        </span>
      </div>

      {topics.map((topic) => (
        <div className="pgs-row" key={topic}>
          <span className="pgs-rowt">
            <b>{topic}</b>
            <small>Aviso quando sair algo novo</small>
          </span>
          <button
            type="button"
            className="pgs-btn small"
            aria-label={`Parar de avisar sobre ${topic}`}
            disabled={busy}
            onClick={() => void save(topics.filter((item) => item !== topic))}
          >
            Parar
          </button>
        </div>
      ))}

      {topics.length < MAX && (
        <form className="na-form" onSubmit={add}>
          <input
            className="na-input"
            value={draft}
            maxLength={60}
            placeholder="Ex.: reforma tributária, Flamengo, dólar"
            aria-label="Assunto do alerta"
            onChange={(event) => setDraft(event.target.value)}
          />
          <button
            type="submit"
            className="pgs-btn solid"
            disabled={busy || (pro && draft.trim().length < 3)}
          >
            {pro ? 'Avisar' : `No plano ${PLAN_CATALOG.pro.name}`}
          </button>
        </form>
      )}
      {error && (
        <p className="na-error" role="alert">
          {error}
        </p>
      )}

      {items.slice(0, 12).map((item) => (
        <a
          key={item.id}
          className="pgs-row na-row"
          href={item.url ?? undefined}
          target="_blank"
          rel="noreferrer"
        >
          <span className="pgs-rowt">
            <b>{item.title}</b>
            <small>
              {item.summary ?? 'Alerta'} · {when(item.createdAt)}
            </small>
          </span>
          <span className="na-go" aria-hidden="true">
            ↗
          </span>
        </a>
      ))}
      {pro && topics.length > 0 && items.length === 0 && (
        <p className="pgs-empty">Nada novo ainda. Assim que sair, aparece aqui.</p>
      )}
    </section>
  );
}
